import React, { useState, useRef, useEffect, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const DropdownContext = createContext();

export function Dropdown({ children, className = '' }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        if (event.target.closest && event.target.closest('.dropdown-portal')) return;
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <DropdownContext.Provider value={{ isOpen, setIsOpen, triggerRef: dropdownRef }}>
      <div ref={dropdownRef} className={`relative text-left ${className || 'w-full'}`}>
        {children}
      </div>
    </DropdownContext.Provider>
  );
}

export function DropdownTrigger({ children, className = '' }) {
  const { isOpen, setIsOpen } = useContext(DropdownContext);
  return (
    <div onClick={() => setIsOpen(!isOpen)} className={`cursor-pointer ${className}`}>
      {children}
    </div>
  );
}

export function DropdownPopover({ children, className = '', placement = 'bottom' }) {
  const { isOpen, setIsOpen, triggerRef } = useContext(DropdownContext);
  const [coords, setCoords] = useState(null);

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      let finalPlacement = placement;
      if (placement === 'bottom' && spaceBelow < 300 && spaceAbove > spaceBelow) {
         finalPlacement = 'top';
      } else if (placement === 'top' && spaceAbove < 300 && spaceBelow > spaceAbove) {
         finalPlacement = 'bottom';
      }

      const isTop = finalPlacement === 'top';
      // Anchor to whichever edge leaves room to grow. The popover is no longer
      // locked to the trigger's width (see style below), so a menu opened by a
      // narrow trigger on the right of the screen must expand leftwards or it
      // runs off the page.
      const anchorRight = rect.left > window.innerWidth / 2;
      setCoords({
        top: isTop ? rect.top : rect.bottom + 8,
        left: rect.left,
        right: window.innerWidth - rect.right,
        width: rect.width,
        anchorRight,
        // Room from the anchored edge to the far side of the viewport, less a
        // small gutter — the ceiling on how wide the menu may grow.
        maxWidth: Math.max(160, (anchorRight ? rect.right : window.innerWidth - rect.left) - 12),
        isTop
      });
    }
  }, [isOpen, triggerRef, placement]);

  useEffect(() => {
    const handleScroll = (e) => {
      if (e.target.closest && e.target.closest('.dropdown-portal')) return;
      setIsOpen(false);
    };
    if (isOpen) {
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleScroll);
    }
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen, setIsOpen]);

  const popover = (
    <AnimatePresence>
      {isOpen && coords && (
        <motion.div
          initial={{ opacity: 0, y: coords.isTop ? 10 : -10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: coords.isTop ? 10 : -10, scale: 0.95 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className={`dropdown-portal fixed z-[99999] rounded-xl bg-white shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none ${className}`}
          // Width follows the CONTENT, floored at the trigger's width and
          // capped by the room left in the viewport. It used to be pinned to
          // the trigger exactly, which truncated every option longer than the
          // button that opened it — "Instagra…", "Hassan Ri…", "Lead Creation
          // …" — across the leads, call list, contacts and dashboard filters.
          style={{
            top: coords.isTop ? 'auto' : coords.top,
            bottom: coords.isTop ? window.innerHeight - coords.top + 8 : 'auto',
            left: coords.anchorRight ? 'auto' : coords.left,
            right: coords.anchorRight ? coords.right : 'auto',
            minWidth: Math.max(120, coords.width),
            maxWidth: coords.maxWidth,
            width: 'max-content',
          }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );

  return typeof document !== 'undefined' ? createPortal(popover, document.body) : null;
}

export function DropdownMenu({ children, selectedKeys = new Set(), onSelectionChange, selectionMode = 'single', className = '' }) {
  // Pass selection state to children via context or cloning
  return (
    <div className={`py-1 max-h-48 overflow-y-auto overscroll-contain ${className}`} role="menu" aria-orientation="vertical">
      {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, { selectedKeys, onSelectionChange, selectionMode });
        }
        return child;
      })}
    </div>
  );
}

export function DropdownSection({ title, children, selectedKeys, onSelectionChange, selectionMode, className = '' }) {
  return (
    <div className={`py-1 ${className}`}>
      {title && <div className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">{title}</div>}
      {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, { selectedKeys, onSelectionChange, selectionMode });
        }
        return child;
      })}
    </div>
  );
}

export function DropdownItem({ id, children, selectedKeys, onSelectionChange, selectionMode, className = '' }) {
  const isSelected = selectedKeys?.has(id);
  const context = useContext(DropdownContext);

  const handleClick = () => {
    if (!onSelectionChange) {
      if (context?.setIsOpen) context.setIsOpen(false);
      return;
    }

    if (selectionMode === 'multiple') {
      const newSet = new Set(selectedKeys);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      onSelectionChange(newSet);
    } else {
      onSelectionChange(new Set([id]));
      if (context?.setIsOpen) context.setIsOpen(false);
    }
  };

  return (
    <div
      className={`relative flex items-center gap-2 px-3 py-2 mx-1 rounded-lg cursor-pointer text-sm transition-colors
        ${isSelected ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}
        ${className}`}
      onClick={handleClick}
    >
      <div className="flex-shrink-0 w-4 h-4 flex items-center justify-center">
        {isSelected && <Check className="w-4 h-4" strokeWidth={3} />}
      </div>
      <div className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">
        {children}
      </div>
    </div>
  );
}
